import {DataRegions} from "@/lib/constants/account";
import {BillingPeriod} from "@/lib/types/billing.ts";
import {DateFormatName, TimeFormatName} from "@/lib/types/common.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";
import {MonitoringGroupLevel} from "@/lib/types/monitoring.ts";
import {IDPInfo, SAMLSettings} from "@/lib/types/statuspages.ts";

export enum TwoFactorOverride {
  ACCOUNT_DEFAULT = "ACCOUNT_DEFAULT",
  REQUIRE = "REQUIRE",
  DO_NOT_REQUIRE = "DO_NOT_REQUIRE",
}

export const TwoFactorOverrideLabels: Record<TwoFactorOverride, string> = {
  [TwoFactorOverride.ACCOUNT_DEFAULT]: "Account Default",
  [TwoFactorOverride.REQUIRE]: "Require",
  [TwoFactorOverride.DO_NOT_REQUIRE]: "Do Not Require",
};

export enum UserAccessLevel {
  READ = "10-READ",
  WRITE = "30-WRITE",
  ADMIN = "50-ADMIN",
}

export const UserAccessLevelLabels: Record<UserAccessLevel, string> = {
  [UserAccessLevel.READ]: "View Only",
  [UserAccessLevel.WRITE]: "View & Modify",
  [UserAccessLevel.ADMIN]: "Administrator",
};

export const displayForUserAccessLevel = (user: AccountUser): string =>
  user.is_primary ? "Owner" : UserAccessLevelLabels[user.access_level];

export enum AccountType {
  REGULAR = "REGULAR",
  INVOICED = "INVOICED",
  INTERNAL = "INTERNAL",
  PARTNER = "PARTNER",
  COMPLIMENTARY = "COMPLIMENTARY",
}

export enum AccountSubscriptionType {
  BILLING_1_0 = "BILLING_1_0",
  BILLING_2_0 = "BILLING_2_0",
}

export interface SSOAccountUserBinding {
  type: string;
  sso_username: string;
}

export interface PlanDefinition {
  limit_api_checks: number;
  limit_basic_checks: number;
  limit_advanced_checks: number;
  limit_custom_checks: number;
  limit_dashboards: number;
  limit_group_checks: number;
  limit_history: number;
  limit_monitoring_group_level: MonitoringGroupLevel;
  limit_monitoring_groups: number;
  limit_multi_users: number;
  limit_pagespeed_checks: number;
  limit_phonecalls: number;
  limit_sms_and_phone_alerts: number;
  limit_private_locations: number;
  limit_reports: number;
  limit_rum_checks_requests_per_month: number;
  limit_sms: number;
  limit_statuspage_subscriptions: number;
  limit_statuspages: number;
  limit_statuspages_external_user: number;
  limit_transaction_checks: number;
  limit_check_frequency: CheckFrequency;
  enable_advanced_features: boolean;
  enable_api_access: boolean;
  enable_audit_log: boolean;
  enable_enterprise_features: boolean;
  enable_international_sms: boolean;
  enable_sso: boolean;
  enable_statuspages: boolean;
  enable_string_matching: boolean;
  enable_transaction_ext_checks: boolean;
}

export interface Account extends PlanDefinition {
  id: number;
  name: string;
  phone: string;
  address: string;
  billing_address?: string;
  country: string;
  city?: string;
  zipcode?: string;
  additional_info?: string;
  data_region: DataRegions;
  require_two_factor: boolean;
  free_trial_expires_at: string;
  has_can_purchase_feature: boolean;
  primary_user_name: string;
  primary_user_email: string;
  type: AccountType;
  plan: string;
  billing_period?: BillingPeriod;
  timezone: string;
  is_subaccount: boolean;
  is_mobile_enabled: boolean;
  status_pages_use_spa: boolean;
  enable_public_link_sharing: boolean;
  disable_regular_login?: boolean;
  has_sso_configured: boolean;
  converted_at?: Date;
  feature_flags: AccountFeatureFlags[];
  subscription_type: AccountSubscriptionType;
  inactive_session_timeout?: number;
}

export interface AccountFeatureFlags {
  name: string;
}

export interface AccountItems {
  items: Account[];
  total: number;
}

export interface AccountUserBase {
  id?: number;
  first_name: string;
  last_name: string;
  email: string;
  access_level: UserAccessLevel;
  assigned_subaccounts: Account[];
  is_api_enabled: boolean;
  is_mobile_enabled: boolean;
  is_primary: boolean;
  has_purchase_permission: boolean;
  require_two_factor: TwoFactorOverride;
  notify_paid_invoices: boolean;
  notify_important_updates: boolean;
}

export interface AccountUser extends Omit<AccountUserBase, "id"> {
  id: number;
  username: string;
  account: Account;
  is_active: boolean;
  has_enterprise_features: boolean;
  has_write_access: boolean;
  has_admin_access: boolean;
  has_advanced_features: boolean;
  has_purchase_permission: boolean;
  must_two_factor: boolean;
  has_two_factor_device: boolean;
  whatsnew_read_at: Date;
  whatsnew_updated_at: Date;
  current_date_format: DateFormatName;
  current_time_format: TimeFormatName;
  sso_user_bindings: SSOAccountUserBinding[];
  last_login: string;
  ui_settings: UiSettings;
}

export type UiSettings = {
  table_page_size: number;
};

export enum OnboardingWizardStep {
  WELCOME = "WELCOME",
  ALERTS = "ALERTS",
  TEAM = "TEAM",
  CONNECTED = "CONNECTED",
  DONE = "DONE",
}

export interface AccountSignupAnalyticsData {
  id: number;
  monitor_my_website: boolean;
  status_page: boolean;
  website_monitoring: boolean;
  page_speed_monitoring: boolean;
  api_monitoring: boolean;
  onboarding_wizard_step: string;
  service_id: number;
  service_domain: string;
}

export interface AccountSignupAnalyticsDataResult {
  accountSignupAnalyticsData: AccountSignupAnalyticsData;
}

export interface PlanLimit {
  used: number;
  included: number;
  limit: number;
  exhausted: boolean;
  allowed: number;
  bundleSize: number;
}

export interface PlanLimitCheckFrequency {
  used: CheckFrequency;
  included: CheckFrequency;
}

export interface PlanLimitMonitoringGroupLevel {
  used: MonitoringGroupLevel;
  included: MonitoringGroupLevel;
}

export interface PlanLimits {
  limit_api_checks: PlanLimit;
  limit_advanced_checks: PlanLimit;
  limit_basic_checks: PlanLimit;
  limit_cloudstatus_checks: PlanLimit;
  limit_custom_checks: PlanLimit;
  limit_group_checks: PlanLimit;
  limit_history: PlanLimit;
  limit_monitoring_group_level: PlanLimitMonitoringGroupLevel;
  limit_monitoring_groups: PlanLimit;
  limit_multi_users: PlanLimit;
  limit_pagespeed_checks: PlanLimit;
  limit_phonecalls: PlanLimit;
  limit_sms_and_phone_alerts: PlanLimit;
  limit_private_locations: PlanLimit;
  limit_rum_checks_requests_per_month: PlanLimit;
  limit_sms: PlanLimit;
  limit_statuspage_subscriptions: PlanLimit;
  limit_statuspages: PlanLimit;
  limit_statuspages_external_user: PlanLimit;
  limit_transaction_checks: PlanLimit;
  limit_check_frequency: PlanLimitCheckFrequency;
}

export interface PlanInfo {
  name: string;
  price_monthly: number;
  price_annual: number;
  isOverLimit: boolean;
  limits: PlanLimits;
}

export interface AccountSAMLSettingsResult {
  accountSAMLSettings: SAMLSettings;
  testIDPs: {
    items: IDPInfo[];
  };
}
